import "./UpcomingEventTile.css";

import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Alarm from "~/content/icons/alarm.svg?react";
import { formatTimeWindow, toPrettyLocaleDateString } from "~/helpers/date-helpers";
import { formatDurationToNow } from "~/helpers/relative-time-helpers";

const dateOptions = {
  month: "long",
  day: "numeric",
};

const buildRangeLabel = (
  availableStart,
  availableEnd,
  timingWindowStart,
  timingWindowEnd,
) => {
  const startDate = toPrettyLocaleDateString(availableStart, dateOptions);
  const endDate = toPrettyLocaleDateString(availableEnd, dateOptions);

  if (!timingWindowStart || !timingWindowEnd) {
    return `${startDate} – ${endDate}`;
  }

  const [startTime, endTime] = formatTimeWindow(
    timingWindowStart,
    timingWindowEnd,
  ).split(" - ");
  return `${startDate}, ${startTime} – ${endDate}, ${endTime}`;
};

export function UpcomingEventTile({ event, now }) {
  const { t } = useTranslation();

  const targetDate = toPrettyLocaleDateString(event.target, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const duration = formatDurationToNow(event.scheduledStartDateTime, now);
  const availableInLabel = duration
    ? t("home.event.available", { duration })
    : null;

  const hasRange = !!event.availableStart && !!event.availableEnd;
  const rangeLabel = hasRange
    ? buildRangeLabel(
        event.availableStart,
        event.availableEnd,
        event.timingWindowStart,
        event.timingWindowEnd,
      )
    : null;

  return (
    <section className="upcoming-event-tile" data-testid="upcoming-event-tile">
      <div className="upcoming-event-tile__heading">
        <h2 className="upcoming-event-tile__form-name">{event.formName}</h2>
        {event.target && (
          <p className="upcoming-event-tile__target-date">
            {t("home.event.for_date", { date: targetDate })}
          </p>
        )}
      </div>
      {(availableInLabel || rangeLabel) && (
        <div className="upcoming-event-tile__footer">
          <Alarm aria-hidden="true" className="upcoming-event-tile__icon" />
          <div className="upcoming-event-tile__details">
            {availableInLabel && (
              <p className="upcoming-event-tile__available-in">
                {availableInLabel}
              </p>
            )}
            {rangeLabel && (
              <p className="upcoming-event-tile__range">{rangeLabel}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

UpcomingEventTile.propTypes = {
  event: PropTypes.shape({
    formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    formName: PropTypes.string.isRequired,
    target: PropTypes.string,
    availableStart: PropTypes.string,
    availableEnd: PropTypes.string,
    timingWindowStart: PropTypes.string,
    timingWindowEnd: PropTypes.string,
  }).isRequired,
  now: PropTypes.string,
};
